import * as THREE from 'three';
import {
  computeBoundingBox,
  disposeAllChildren,
  disposeAllChildrenThenRemoveFromParent,
  setModelVisible
} from '../utils';
import { globalStores } from '@/editor/stores';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { pixelRatio3d } from '@/editor/math/const';
import { IModelEntityInfo } from '@/editor/types/entity';
import { ESceneMode } from '@/editor/types';

const placeholderFillMaterial = new THREE.MeshBasicMaterial({
  color: 0xf28600,
  opacity: 0.1,
  transparent: true,
  depthTest: true,
  depthWrite: false
});

interface IModelInfo {
  model: THREE.Object3D | null;
  loaded: boolean;
  callbacks: ((isError: boolean, scene?: THREE.Object3D) => void)[];
}

class BaseModel3d {
  protected _id: string;
  protected _isDisposed = false; // entity 要销毁时，要设置为 true，此时不会再进行模型加载
  protected _modelUrl: string = '';
  protected _originBoundingBox: number[] = [0, 0, 0];
  protected _originRotationZ: number = 0;
  protected _placeholderGroup: THREE.Group | null = null;

  model: THREE.Group;

  // 状态标志
  isLoaded: boolean;
  isLoading: boolean;
  isInitialized: boolean;

  constructor(options: { id: string; userData?: IModelEntityInfo }) {
    const { id } = options;

    // 初始化逻辑
    this._id = id;

    this.model = new THREE.Group();
    this.model.userData = options.userData || {};

    this.isLoaded = false;
    this.isLoading = false;
    this.isInitialized = true;

    // 添加到场景
    // globalStores.scene3dInfo.scene.add(this.model);
  }

  // 后续优化，目前没有删除逻辑
  static modelCache: Map<string, IModelInfo> = new Map();

  static modelLoader(url: string, cb: IModelInfo['callbacks'][number]) {
    const modelInfo = this.modelCache.get(url);

    if (modelInfo && modelInfo.loaded && modelInfo.model) {
      cb(false, modelInfo.model);
    } else if (modelInfo) {
      modelInfo.callbacks.push(cb);
    } else {
      const newModelInfo = {
        model: null,
        loaded: false,
        callbacks: [cb]
      } as IModelInfo;
      this.modelCache.set(url, newModelInfo);

      const gltfLoader = new GLTFLoader();
      gltfLoader.setMeshoptDecoder(MeshoptDecoder);
      gltfLoader.load(
        url,
        (gltfData: GLTF) => {
          const scene = gltfData.scene;

          newModelInfo.model = scene;
          newModelInfo.loaded = true;

          newModelInfo.callbacks.forEach((cb) => {
            cb(false, scene);
          });
          newModelInfo.callbacks.length = 0;
        },
        undefined,
        (error: unknown) => {
          console.error('GLTF加载失败:', error);

          newModelInfo.callbacks.forEach((cb) => {
            cb(true);
          });
          newModelInfo.callbacks.length = 0;
          newModelInfo.model = null;
        }
      );
    }
  }

  get id() {
    return this._id;
  }

  get userData() {
    return this.model.userData;
  }

  get canUpdate() {
    return globalStores.sceneMode === ESceneMode.scene3d;
  }

  private addToScene(gltfScene: THREE.Object3D) {
    // 下移半高，与坐标轴中心点对齐
    gltfScene.position.set(0, 0, -this._originBoundingBox[2] / 2 / pixelRatio3d);

    if (this._originRotationZ && this._originRotationZ !== 0) {
      // 如果元素模型有旋转，需要还原回来
      gltfScene.rotation.z = -this._originRotationZ;
    }

    this.model.add(gltfScene);
    this.disposePlaceholder();
  }

  /**
   * 把模型的 clone 挂到 this.model 下，缓存里没有时先下载
   * @returns 本次调用是否挂上了模型；无模型地址、无体积、已在加载中或已销毁时为 false
   * @throws 下载失败时 reject
   */
  async loadModel() {
    const noVolume = this._originBoundingBox.some((item) => item === 0);

    if (!this._modelUrl || noVolume || this.isLoading || this._isDisposed)
      return new Promise<boolean>((resolve) => resolve(false));

    return new Promise<boolean>((resolve, reject) => {
      const modelInfo = BaseModel3d.modelCache.get(this._modelUrl);
      if (modelInfo && modelInfo.loaded && modelInfo.model) {
        // 有缓存模型
        this.addToScene(modelInfo.model.clone());
        this.isLoaded = true;
        resolve(true);
        return;
      }

      const loadedCallback = (isError: boolean, gltf?: THREE.Object3D) => {
        // 下载期间实体被销毁是正常路径（如 3D 视角隐藏墙时移除其上门窗），按未加载处理而非报错
        if (this._isDisposed) {
          resolve(false);
          return;
        }
        if (isError || !gltf) {
          reject(new Error(`模型加载失败: ${this._modelUrl}`));
          return;
        }

        this.addToScene(gltf.clone());
        this.isLoading = false;
        this.isLoaded = true;
        resolve(true);
      };

      if (modelInfo) {
        // modelLoader 正在下载中, 设置isLoading为true，防止二次进入
        this.isLoading = true;
        modelInfo.callbacks.push(loadedCallback);
        return;
      }

      // 首次下载
      BaseModel3d.modelLoader(this._modelUrl, loadedCallback);
    });
  }

  setVisible(visible: boolean) {
    setModelVisible(this.model, visible);
  }

  getBoundingBox() {
    return computeBoundingBox(this.model);
  }

  protected updatePlaceholder() {
    const [width, thickness, height] = this._originBoundingBox;

    if (width <= 0 || thickness <= 0 || height <= 0 || this.isLoaded) {
      this.disposePlaceholder();
      return;
    }

    const group = this.ensurePlaceholderGroup();
    disposeAllChildren(group);

    const widthWorld = width / pixelRatio3d;
    const thicknessWorld = thickness / pixelRatio3d;
    const heightWorld = height / pixelRatio3d;

    const fillGeometry = new THREE.BoxGeometry(widthWorld, thicknessWorld, heightWorld);
    const fillMesh = new THREE.Mesh(fillGeometry, placeholderFillMaterial);
    group.add(fillMesh);

    group.position.set(0, 0, 0);
  }

  protected disposePlaceholder() {
    if (!this._placeholderGroup) return;
    disposeAllChildrenThenRemoveFromParent(this._placeholderGroup);
    this._placeholderGroup = null;
  }

  private ensurePlaceholderGroup() {
    if (!this._placeholderGroup) {
      this._placeholderGroup = new THREE.Group();
      this.model.add(this._placeholderGroup);
    } else if (this._placeholderGroup.parent !== this.model) {
      this.model.add(this._placeholderGroup);
    }

    this._placeholderGroup.visible = true;
    return this._placeholderGroup;
  }

  // 清空所有子节点，model节点不改变
  clearModel() {
    disposeAllChildren(this.model);
    this.model.scale.set(1, 1, 1);
    this.isLoaded = false;
    this.disposePlaceholder();
  }

  dispose() {
    this._isDisposed = true;

    // 停止加载过程
    this.isLoading = false;

    // 清理模型资源
    disposeAllChildrenThenRemoveFromParent(this.model);
    this.disposePlaceholder();
  }
}

export default BaseModel3d;
